/**
 * Porta de integração com o CRM (Dynamics 365) e mapeamento do documento para a Cotação.
 *
 * Este módulo é **puro**: não faz rede, não conhece o Power Automate e não importa SDK algum. Ele
 * define o contrato e a tradução. Quem executa a chamada HTTP é o adaptador em `services/api`, que
 * recebe uma implementação desta interface.
 *
 * A razão de a tradução morar aqui, e não no adaptador, é a decisão D3 aplicada ao CRM: o painel
 * precisa mostrar ao vendedor exatamente o que será enviado ao Dynamics, antes de publicar. Se o
 * mapeamento vivesse na Lambda, o painel teria de reimplementá-lo, e as duas versões divergiriam.
 *
 * O sentido inverso — CRM para Construtor — está em `importarOportunidade`, usado quando o vendedor
 * informa o número da oportunidade no primeiro campo do formulário (decisão D13).
 */

import {
  exigirOportunidade,
  type DocumentoProposta,
  type OportunidadeCrm,
  type SincronizacaoCrm,
} from './documento.ts';
import { arredondar } from './dinheiro.ts';
import { descricaoExibivel } from './csv-calculadora.ts';
import { NOME_COMERCIAL_SAFESTORAGE } from './configuracao.ts';

// ---------------------------------------------------------------------------
// Payload enviado ao CRM
// ---------------------------------------------------------------------------

/**
 * Linha da Cotação.
 *
 * O Dynamics distingue produto do catálogo de produto *write-in* (ad hoc) pelo campo
 * `isproductoverridden`. Serviços recorrentes da eMaster — SafePlace, assessment, treinamento — são
 * de catálogo e têm SKU. Recursos AWS são write-in: criar um produto para cada combinação de
 * serviço, região, família, tamanho e modelo de compra produziria um catálogo impossível de manter.
 */
export interface LinhaCotacao {
  /** Chave estável da linha, para idempotência e para reconciliar em revisões. */
  readonly idExternoLinha: string;
  /** SKU do catálogo. Nulo em linha write-in. */
  readonly sku: string | null;
  readonly descricao: string;
  /** Detalhamento técnico. Em recurso AWS, é a memória de cálculo. */
  readonly detalhe: string;
  readonly quantidade: number;
  readonly precoUnitario: number;
  readonly total: number;
  /** `true` para linha write-in, que vira `isproductoverridden` no `quotedetail`. */
  readonly writeIn: boolean;
  /** Metadados do recurso AWS, para os campos customizados. Nulo em produto de catálogo. */
  readonly aws: {
    readonly servico: string;
    readonly regiao: string | null;
    readonly grupo: string;
  } | null;
}

/** Payload completo, na forma que o fluxo do Power Automate consome. */
export interface PayloadCotacao {
  /** Chave externa idempotente. É o número da proposta. */
  readonly idPropostaExterna: string;
  /** Revisão da proposta. Junto com o id externo, forma a chave lógica no Dataverse. */
  readonly revisao: number;
  /** GUID da oportunidade. Obrigatório: a Cotação é criada por GUID, nunca por nome. */
  readonly oportunidadeId: string;
  readonly oportunidadeNumero: string;
  readonly contaId: string | null;
  readonly nome: string;
  readonly moeda: 'USD' | 'BRL';
  readonly listaPrecoId: string | null;
  readonly vigenciaInicio: string;
  readonly vigenciaFim: string;
  readonly descricao: string;
  readonly condicoesComerciais: string;
  readonly propostaUrl: string | null;
  readonly linhas: readonly LinhaCotacao[];
  readonly totais: {
    readonly servicosEmaster: number;
    readonly recursosAws: number;
    readonly total: number;
  };
  /** Situação comercial no momento do envio. */
  readonly situacao: 'RASCUNHO' | 'PUBLICADA' | 'ACEITA';
  /** Hash do conteúdo, para detectar reenvio de payload idêntico. */
  readonly hashConteudo: string;
}

// ---------------------------------------------------------------------------
// Resultado e porta
// ---------------------------------------------------------------------------

/** Códigos de desfecho que o fluxo devolve. Espelham a matriz de erros do plano de integração. */
export type CodigoResultadoCrm =
  | 'SINCRONIZADA'
  | 'JA_SINCRONIZADA'
  | 'CONFLITO_DE_VERSAO'
  | 'OPORTUNIDADE_NAO_ENCONTRADA'
  | 'MOEDA_INCOMPATIVEL'
  | 'DIVERGENCIA_DE_TOTAL'
  | 'ERRO_TECNICO';

export interface ResultadoCrm {
  readonly codigo: CodigoResultadoCrm;
  readonly cotacaoId: string | null;
  readonly cotacaoNumero: string | null;
  readonly correlacaoId: string | null;
  readonly mensagem: string | null;
}

/** Dados que o CRM devolve ao resolver um número de oportunidade. */
export interface OportunidadeImportada {
  readonly id: string;
  readonly numero: string;
  readonly nome: string;
  readonly contaId: string | null;
  readonly contaNome: string | null;
  readonly moeda: 'USD' | 'BRL' | null;
  readonly listaPrecoId: string | null;
  readonly proprietarioEmail: string | null;
}

/**
 * Porta do CRM.
 *
 * Duas operações, nos dois sentidos do fluxo. Manter as duas na mesma interface é deliberado: um
 * adaptador que só saiba enviar produziria a experiência que a decisão D13 rejeita, em que o vendedor
 * digita à mão o que o CRM já sabe.
 */
export interface CrmGateway {
  /** Resolve o número informado pelo vendedor nos dados da oportunidade. */
  importarOportunidade(numero: string): Promise<OportunidadeImportada | null>;
  /** Cria ou atualiza a Cotação correspondente à proposta. Precisa ser idempotente. */
  sincronizarCotacao(payload: PayloadCotacao): Promise<ResultadoCrm>;
  /** Anexa o PDF da proposta formal à Cotação. */
  anexarDocumento(
    cotacaoId: string,
    nomeArquivo: string,
    pdfBase64: string,
  ): Promise<{ readonly url: string | null }>;
}

/**
 * Implementação nula.
 *
 * Existe para que a publicação funcione **antes** de o conector real existir, sem `if` espalhado pelo
 * handler. `importarOportunidade` devolve `null`, o que o formulário trata como "número não
 * resolvido"; `sincronizarCotacao` devolve `ERRO_TECNICO` com mensagem explícita, o que grava a
 * situação correta no documento em vez de fingir sucesso.
 *
 * Fingir sucesso seria pior que falhar: a proposta ficaria marcada como sincronizada sem Cotação
 * alguma no CRM, e ninguém descobriria até o fechamento do mês.
 */
export function crmGatewayNulo(): CrmGateway {
  return {
    importarOportunidade: () => Promise.resolve(null),

    sincronizarCotacao: () =>
      Promise.resolve({
        codigo: 'ERRO_TECNICO',
        cotacaoId: null,
        cotacaoNumero: null,
        correlacaoId: null,
        mensagem:
          'O conector do Dynamics 365 ainda não está configurado. A proposta foi publicada, mas nenhuma Cotação foi criada.',
      }),

    anexarDocumento: () => Promise.resolve({ url: null }),
  };
}

// ---------------------------------------------------------------------------
// Tradução do documento para o payload
// ---------------------------------------------------------------------------

/**
 * Hash estável do conteúdo comercial, usado para detectar reenvio idêntico.
 *
 * Implementação FNV-1a de 32 bits em hexadecimal. Não é criptográfico e não precisa ser: o objetivo é
 * comparar dois payloads, não resistir a colisão adversarial. Usar `crypto` traria dependência de
 * plataforma e violaria a fronteira do pacote (decisões D3 e D6).
 */
export function hashConteudo(entrada: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < entrada.length; i += 1) {
    h ^= entrada.charCodeAt(i);
    // Multiplicação por 16777619 em 32 bits, sem estourar o inteiro seguro do JavaScript.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Frases fixas das condições comerciais, montadas a partir dos campos estruturados. */
export function condicoesComerciaisTexto(doc: DocumentoProposta): string {
  const moeda = doc.premissas.trilha === 'PS' ? 'Real' : 'US Dollar';
  const linhas = [
    `Prazo contratual de locação: ${String(doc.premissas.duracaoContratoMeses)} meses`,
    `Valores apresentados em ${moeda}`,
  ];

  const f = doc.formalizacao;
  if (f !== null) {
    if (f.condicoes.impostosInclusos) {
      linhas.push('Os valores incluem os impostos aplicáveis.');
    }
    linhas.push(
      `O faturamento ocorrerá até o dia ${String(f.condicoes.diaFaturamento)} do mês, com vencimento para o dia ${String(f.condicoes.diaVencimento)} ou próximo dia útil.`,
    );
    linhas.push(
      'Serviços cotados em outras moedas serão faturados conforme o câmbio fornecido pela AWS no dia do fechamento da fatura.',
    );
    linhas.push(`Forma de Pagamento: ${f.condicoes.formaPagamento}.`);
    linhas.push('Faturamento mensal efetuado conforme medição dos recursos consumidos no período.');

    const pct = f.condicoes.percentualProjetoImplantacao;
    if (pct !== null) {
      linhas.push(
        `O valor do Projeto de Implantação, cobrado pontualmente, é calculado considerando ${String(pct)}% da estimativa de faturamento acima.`,
      );
    }

    for (const extra of f.condicoes.clausulasAdicionais) {
      linhas.push(extra);
    }
  }

  return linhas.join('\n');
}

/**
 * Traduz o documento no payload da Cotação.
 *
 * Regras que esta função encapsula, e que existem para não deixar o CRM divergir da proposta:
 *
 * - o valor enviado é o **preço de venda**, já com margem, nunca o custo líquido do CSV;
 * - a moeda vem da trilha, não da oportunidade, porque a trilha é o que determinou o cálculo;
 * - recursos AWS entram consolidados por serviço, na mesma granularidade da tabela de investimento,
 *   para que o cliente veja no CRM o que viu na proposta.
 *
 * @throws {ErroPublicacao} quando a oportunidade não está resolvida.
 */
export function documentoParaPayloadCotacao(
  doc: DocumentoProposta,
  propostaUrl: string | null,
): PayloadCotacao {
  const oportunidade = exigirOportunidade(doc);
  const r = doc.resultados;
  const moeda: 'USD' | 'BRL' = doc.premissas.trilha === 'PS' ? 'BRL' : 'USD';

  // Em BRL usamos o valor convertido; em USD, o valor original. Cair para USD quando o BRL é nulo
  // seria gravar valor em moeda errada, então a ausência é tratada como zero e a conferência de
  // total no fluxo detecta o problema.
  const naMoeda = (v: { usd: number; brl: number | null }): number =>
    moeda === 'BRL' ? (v.brl ?? 0) : v.usd;

  const linhas: LinhaCotacao[] = [];
  let totalAws = 0;
  let totalEmaster = 0;

  // --- Recursos AWS: write-in, um por item do CSV, com a margem já aplicada.
  const itens = doc.premissas.itensCusto;
  if (r.margemAplicada && itens !== null && itens.length > 0) {
    const fator = 1 - r.margemPercentual / 100;
    itens.forEach((item, indice) => {
      const vendaUsd = arredondar(item.custoMensalLiquidoUsd / fator);
      const valor =
        moeda === 'BRL' && doc.premissas.ptax !== null
          ? arredondar(vendaUsd * doc.premissas.ptax)
          : vendaUsd;

      // O `detalhe` vira o campo `description` da linha da Cotação. Leva a descrição do CSV junto
      // com a memória de cálculo para que o vendedor veja no Dynamics o mesmo texto da proposta.
      const contexto = descricaoExibivel(item.descricao);
      const detalhe = [contexto, item.resumoConfiguracao].filter((t) => t !== '').join(' — ');

      totalAws += valor;
      linhas.push({
        idExternoLinha: `AWS-${String(indice + 1).padStart(3, '0')}`,
        sku: null,
        descricao: item.servico,
        detalhe,
        quantidade: 1,
        precoUnitario: valor,
        total: valor,
        writeIn: true,
        aws: { servico: item.servico, regiao: doc.premissas.regiaoAws, grupo: item.grupo },
      });
    });
  } else if (r.consumo.usd > 0) {
    const valor = naMoeda(r.consumo);
    totalAws += valor;
    linhas.push({
      idExternoLinha: 'AWS-001',
      sku: null,
      descricao: 'Infraestrutura AWS',
      detalhe: r.margemAplicada
        ? `Dimensionada com margem de gestão de ${String(r.margemPercentual)}%`
        : 'Conforme dimensionamento acordado',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      writeIn: true,
      aws: { servico: 'Consolidado', regiao: doc.premissas.regiaoAws, grupo: 'Infraestrutura' },
    });
  }

  // --- Plano de suporte: produto de catálogo, com SKU por faixa.
  if (r.custoSuporte.usd > 0) {
    const valor = naMoeda(r.custoSuporte);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-SUPORTE',
      sku: `SAFEPLACE-${doc.premissas.planoSuporte.toUpperCase()}`,
      descricao: `Plano de suporte SafePlace ${doc.premissas.planoSuporte}`,
      detalhe: 'Sustentação, monitoramento e acompanhamento consultivo',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      writeIn: false,
      aws: null,
    });
  }

  // --- Licenças de backup: produto de catálogo.
  if (r.licencasBackup.usd > 0) {
    const valor = naMoeda(r.licencasBackup);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-BACKUP',
      sku: 'EMASTER-BACKUP-ONPREM',
      descricao: 'eMaster Backup On-Premises',
      detalhe: 'Licenças mensais conforme quantidade contratada',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      writeIn: false,
      aws: null,
    });
  }

  /*
   * SafeStorage: produto de catálogo, com SKU por faixa.
   *
   * A quantidade continua 1 e o preço é o da assinatura, não o unitário por usuário. A faixa é um
   * degrau, não um múltiplo: 20 usuários custam a Constelação inteira, e lançar 20 × preço unitário
   * no CRM produziria uma Cotação com total que não bate com a proposta.
   *
   * A contagem de usuários vive em `detalhe`, que é onde o financeiro precisa dela para conferir o
   * degrau contratado.
   */
  if (r.plataformaSafeStorage.usd > 0 && doc.premissas.safestorage !== null) {
    const s = doc.premissas.safestorage;
    const valor = naMoeda(r.plataformaSafeStorage);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-SAFESTORAGE',
      sku: `EMASTER-SAFESTORAGE-${s.plano.toUpperCase()}`,
      descricao: NOME_COMERCIAL_SAFESTORAGE[s.plano],
      detalhe: `Assinatura mensal da plataforma para ${String(s.usuarios)} ${
        s.usuarios === 1 ? 'usuário' : 'usuários'
      }. Armazenamento S3 faturado sob demanda, na linha de infraestrutura.`,
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      writeIn: false,
      aws: null,
    });
  }

  const total = arredondar(totalAws + totalEmaster);

  // A situação enviada ao CRM reflete o estágio comercial, não o estágio de edição: uma proposta
  // aceita permanece aceita mesmo que o documento seja reaberto para correção de texto.
  const foiAceita = doc.formalizacao !== null && doc.formalizacao.aceite.assinadoEm !== null;
  const situacao: PayloadCotacao['situacao'] = foiAceita
    ? 'ACEITA'
    : doc.situacao === 'publicada'
      ? 'PUBLICADA'
      : 'RASCUNHO';

  const descricao = doc.conteudo.promessaValor;
  const condicoes = condicoesComerciaisTexto(doc);

  // O hash cobre o que é comercialmente relevante. Trocar uma vírgula do texto editorial não deve
  // marcar a Cotação como divergente; trocar um valor deve.
  const assinatura = JSON.stringify({
    linhas: linhas.map((l) => [l.idExternoLinha, l.total]),
    total,
    moeda,
    vigencia: [doc.emitidaEm, doc.validaAte],
    condicoes,
  });

  return {
    idPropostaExterna: doc.numero,
    revisao: doc.auditoria.versao,
    oportunidadeId: oportunidade.id,
    oportunidadeNumero: oportunidade.numero,
    contaId: oportunidade.contaId,
    nome: oportunidade.nome ?? `${doc.projeto} — ${doc.cliente}`,
    moeda,
    listaPrecoId: oportunidade.listaPrecoId,
    vigenciaInicio: doc.emitidaEm,
    vigenciaFim: doc.validaAte,
    descricao,
    condicoesComerciais: condicoes,
    propostaUrl,
    linhas,
    totais: {
      servicosEmaster: arredondar(totalEmaster),
      recursosAws: arredondar(totalAws),
      total,
    },
    situacao,
    hashConteudo: hashConteudo(assinatura),
  };
}

/**
 * Converte os dados importados do CRM no bloco de oportunidade do documento.
 *
 * `importadoEm` recebe o instante da importação, o que distingue dado vindo do CRM de dado digitado
 * — informação necessária para decidir se vale reconsultar.
 */
export function oportunidadeDeImportacao(
  importada: OportunidadeImportada,
  agora: string,
): OportunidadeCrm {
  return {
    numero: importada.numero,
    id: importada.id,
    nome: importada.nome,
    contaId: importada.contaId,
    contaNome: importada.contaNome,
    moeda: importada.moeda,
    listaPrecoId: importada.listaPrecoId,
    proprietarioEmail: importada.proprietarioEmail,
    importadoEm: agora,
  };
}

/** Traduz o resultado do CRM no bloco de sincronização do documento. */
export function sincronizacaoDeResultado(r: ResultadoCrm, agora: string): SincronizacaoCrm {
  const bemSucedido = r.codigo === 'SINCRONIZADA' || r.codigo === 'JA_SINCRONIZADA';

  const situacao =
    r.codigo === 'SINCRONIZADA' || r.codigo === 'JA_SINCRONIZADA'
      ? 'sincronizada'
      : r.codigo === 'ERRO_TECNICO'
        ? 'erro_tecnico'
        : 'erro_validacao';

  return {
    situacao,
    cotacaoId: r.cotacaoId,
    cotacaoNumero: r.cotacaoNumero,
    correlacaoId: r.correlacaoId,
    sincronizadoEm: bemSucedido ? agora : null,
    erro: bemSucedido ? null : r.mensagem,
  };
}
